import React, { useState } from "react";
import { createStyles } from "@mantine/core";
import { LuPill } from "react-icons/lu";
import { getColor } from "..";
import moment from "moment-timezone";
import "../../../../components/Calendar/calendarSchedule.css";
import MedicationModal from "../../../../components/MedicationModal";
import MarDetailModal from "../../../../components/MarDetailModal";
import { useDisclosure } from "@mantine/hooks";
import {
  firstLetterCap,
  getSpecificMedicationStatus,
  getActiveSlotForDate,
} from "../../../../utils";
import missedMed from "../../../../images/missedMed.png";
import upcommingMed from "../../../../images/upcommingMed.png";
import takenMed from "../../../../images/takenMed.png";
import refusedMed from "../../../../images/refusedMed.png";
import outOfMed from "../../../../images/outOfMed.png";
import unableToTakeMed from "../../../../images/unableToTakeMed.png";
import prnMed from "../../../../images/prnMed.png";
import { FiCheck, FiX, FiAlertTriangle, FiClock } from "react-icons/fi";

const useStyles = createStyles((theme) => ({
  scheduleCard: {
    width: "100%",
    backgroundColor: "#fff",
    cursor: "pointer",
    position: "relative",
    height: "200px",
    padding: "5px",
    borderTop: "1px solid #E5E5E5",
    borderBottom: "1px solid #E5E5E5",
    "& p": {
      margin: "0px",
    },
    "& .card-frequency": {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "6px",
      marginBottom: "2px",
      "& .freq-name": {
        fontSize: "16px",
        fontWeight: 600,
        color: "#404040",
        lineHeight: "16px",
        fontFamily: "Inter",
        wordBreak: "break-word",
        overflowWrap: "break-word",
      },
      "& .narcotic-badge": {
        display: "inline-block",
        backgroundColor: "#FEF3F2",
        color: "#B42318",
        fontSize: "12px",
        fontWeight: 500,
        padding: "2px 8px",
        borderRadius: "16px",
        fontFamily: "Inter",
      },
    },
    "& .card-time": {
      fontSize: "10px",
      color: "#737373",
      fontWeight: 400,
      fontFamily: "Inter",
    },
    "& .Card-med-border": {
      margin: "0 auto",
      width: "100%",
      height: "1px",
      backgroundColor: "#E5E5E5",
      marginTop: "6px",
    },
    "& .med-dosage-group": {
      marginBlock: "10px",
    },
    "& .card-med-name": {
      fontSize: "12px",
      fontWeight: 500,
      color: "#404040",
      fontFamily: "Inter",
    },
    "& .card-med-form": {
      fontSize: "10px",
      fontWeight: 400,
      fontFamily: "Inter",
      color: "#737373",
    },
    "& .card-status": {
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
  },

  borderDiv: {
    margin: "0 auto",
    width: "100%",
    height: "1px",
    backgroundColor: "#E5E5E5",
  },

  prnInfo: {
    fontSize: "11px",
    color: "#555",
  },

  statusRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "6px",
    minWidth: 0,
  },

  statusImg: {
    height: "19px",
    width: "38px",
    flexShrink: 0,
  },

  statusText: {
    fontSize: "12px",
    fontWeight: 500,
    fontFamily: "Inter",
  },

  statusTaken: { color: "#16A34A" },
  statusRefused: { color: "#0086C9" },
  statusUnable: { color: "#FDB022" },
  statusMissed: { color: "#DC2626" },
  statusDefault: { color: "#525252" },
  statusPrn: { color: "#7F56D9" },

  // ── Tinted cell styles (weekly / daily grid view) ────────────────────
  tintedCell: {
    borderRadius: "8px",
    border: "1px solid #E5E5E5",
    display: "flex",
    flexDirection: "column",
    padding: "10px 10px 12px",
    cursor: "pointer",
    minHeight: "138px",
    position: "relative",
    transition: "border-color 0.14s ease, box-shadow 0.14s ease",
  },

  tintedPillRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    paddingBottom: "8px",
  },

  tintedStatusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "3px 10px 3px 4px",
    borderRadius: "999px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    color: "#FFFFFF",
    fontFamily: "Inter",
    maxWidth: "100%",
  },

  tintedPillIcon: {
    width: "14px",
    height: "14px",
    borderRadius: "999px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  tintedDivider: {
    width: "100%",
    height: 0,
    borderTop: "1px solid #E5E5E5",
    margin: 0,
  },

  tintedBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "6px",
    paddingTop: "10px",
    paddingBottom: "6px",
    width: "100%",
  },

  tintedAdminTile: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "4px 6px",
    borderRadius: "6px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E5E5",
    flexWrap: "wrap",
  },
  tintedAdminDosage: {
    fontFamily: "Inter",
    fontSize: "10px",
    fontWeight: 500,
    color: "#737373",
    width: "100%",
    marginTop: "1px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },

  tintedAdminTime: {
    fontFamily: "Inter",
    fontSize: "11px",
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    flex: 1,
    textAlign: "left",
    margin: 0,
  },

  tintedInitialsAvatar: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    borderRadius: "999px",
    fontWeight: 800,
    fontSize: "10px",
    letterSpacing: "0.4px",
    fontFamily: "Inter",
    flexShrink: 0,
    borderWidth: "2px",
    borderStyle: "solid",
  },

  tintedNoAdmin: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    gap: "4px",
  },
  tintedAwaitingText: {
    fontSize: "11px",
    fontWeight: 500,
    fontFamily: "Inter",
    fontStyle: "italic",
    opacity: 0.85,
  },
  tintedAwaitingTime: {
    fontSize: "10px",
    fontWeight: 700,
    fontFamily: "Inter",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },

  tintedMedLabel: {
    paddingBottom: "8px",
    marginBottom: "8px",
    borderBottom: "1px dashed #D4D4D4",
    width: "100%",
  },
  tintedMedStrip: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    width: "100%",
  },
  tintedMedNameRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
  },
  tintedMedPillIconBox: {
    width: "22px",
    height: "22px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  tintedMedPillIconBoxNormal: {
    backgroundColor: "#FFF3DC",
    border: "1px solid #FFE4A8",
  },
  tintedMedPillIconBoxNarcotic: {
    backgroundColor: "#FEE2E2",
    border: "1px solid #FECACA",
  },
  tintedMedName: {
    fontFamily: "Inter",
    fontSize: "11px",
    fontWeight: 700,
    color: "#171717",
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  tintedMedStrengthRow: {
    fontFamily: "Inter",
    fontSize: "10px",
    fontWeight: 500,
    color: "#525252",
    lineHeight: 1.3,
    wordBreak: "break-word",
    textAlign: "center",
  },
  tintedMedAdminRow: {
    fontFamily: "Inter",
    fontSize: "11px",
    fontWeight: 800,
    color: "#171717",
    marginTop: "2px",
    textAlign: "center",
  },
  tintedMedLabelText: {
    fontFamily: "Inter",
    fontSize: "10px",
    fontWeight: 600,
    lineHeight: 1.3,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
  },

  // ── PRN multiple-admins extras badge ─────────────────────────────────────
  tintedExtrasBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    padding: "1px 7px",
    borderRadius: "999px",
    fontSize: "10px",
    fontWeight: 800,
    fontFamily: "Inter",
    letterSpacing: "0.3px",
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1.4,
    color: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(16,24,40,0.18)",
  },
  tintedExtrasBadgeRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "4px",
  },
}));

const getStatusImage = (status) => {
  if (status === "Taken") return takenMed;
  if (status === "Refused") return refusedMed;
  if (status === "Unable to Take") return unableToTakeMed;
  if (status === "Out of Medication") return outOfMed;
  if (status === "Missed") return missedMed;
  return upcommingMed;
};

const ScheduleCard = ({
  date,
  prn,
  data,
  allData,
  onOpen,
  calendar = "weekly",
  scheduledExtras,
  showMedDetails = false,
  timeZone,
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  // For multi-slot scheduled cells: track which card's modal to open
  const [activeSlotCard, setActiveSlotCard] = useState(null);

  const _medicationType = getSpecificMedicationStatus(
    data?.medicationType,
    date,
    data?.timeSlot,
  );
  const _upcoming = "upcoming";

  const { classes } = useStyles();

  const compareDate = (date) => {
    var time1 = moment(date).startOf("day");
    var today = moment().startOf("day");
    if (calendar !== "weekly" && _medicationType === _upcoming) {
      return time1.isAfter(today) ? 1 : -1;
    } else if (time1.isSame(today)) {
      return 0;
    } else if (time1.isAfter(today)) {
      return 1;
    } else {
      return -1;
    }
  };

  const medName = firstLetterCap(data?.form3?.medicationName) || "-";
  const dosage = data?.form3?.dosage
    ? `${data.form3.dosage}${data.form3.unit ? data.form3.unit : ""}`
    : null;
  const medNameDisplay = dosage ? `${medName} (${dosage})` : medName;
  const formDisplay = [data?.form3?.ingestionMode, data?.form3?.dosageForm]
    .filter(Boolean)
    .join(" ");

  const handleClick = (slotCard) => {
    if (prn) return onOpen(data, date);
    const card = slotCard || data;
    const cardStatus = getSpecificMedicationStatus(
      card?.medicationType,
      date,
      card?.timeSlot,
    );
    cardStatus === _upcoming || cardStatus === "Missed"
      ? setIsViewOpen(false)
      : setIsViewOpen(true);
    setActiveSlotCard(slotCard || null);
    open();
  };

  // ── Tinted cell UI for weekly / daily grid ────────────────────────────
  if (calendar === "weekly" || calendar === "daily") {
    const status = prn ? "PRN" : _medicationType;
    const colorSet = getColor(status) || getColor("upcoming");
    const statusLabel = status === "upcoming" ? "Upcoming" : status;

    // PRN extras: multiple admins of same PRN med in same bucket
    const extras = prn && calendar === "daily" ? data?.extras || [] : [];
    const totalCount = 1 + extras.length;

    // Scheduled extras: multiple timeslots of same scheduled med in same bucket
    const schedExtras =
      !prn && calendar === "daily" ? scheduledExtras || [] : [];
    const allScheduledCards =
      schedExtras.length > 0 ? [data, ...schedExtras] : null;

    const buildAdminInfo = (card) => {
      const tl = prn
        ? card?.administeredAt
          ? moment(card.administeredAt)
              .tz(timeZone || moment.tz.guess())
              .format("hh:mm A")
          : ""
        : getActiveSlotForDate(card?.timeSlot, date) || "";
      const name = card?.staff?.name || "";
      const ini = name
        ? name
            .split(" ")
            .filter(Boolean)
            .map((w) => w[0])
            .join("")
            .toUpperCase()
            .slice(0, 2)
        : "";
      return { timeLabel: tl, initials: ini };
    };

    const { timeLabel, initials } = buildAdminInfo(data);
    const isMissed = status === "Missed";
    const isUpcoming = status === "upcoming";
    const showAdminTile = !isMissed && !isUpcoming && (timeLabel || initials);

    // Render a single admin tile row (reused for primary + extras)
    const renderAdminTile = (card, idx) => {
      const { timeLabel: tl, initials: ini } = buildAdminInfo(card);
      if (!tl && !ini) return null;
      const tileClickable = prn && extras.length > 0;
      return (
        <div
          key={idx}
          className={classes.tintedAdminTile}
          style={{
            borderColor: colorSet?.border || "#E5E5E5",
            ...(tileClickable ? { cursor: "pointer" } : {}),
          }}
          onClick={
            tileClickable
              ? (e) => {
                  e.stopPropagation();
                  onOpen(card, date);
                }
              : undefined
          }
        >
          {tl ? (
            <span
              className={classes.tintedAdminTime}
              style={{ color: colorSet?.dark || "#404040" }}
            >
              {tl}
            </span>
          ) : null}
          {ini ? (
            <div
              className={classes.tintedInitialsAvatar}
              style={{
                backgroundColor: colorSet?.light || "#F5F5F5",
                color: colorSet?.dark || "#404040",
                borderColor: colorSet?.dark || "#E5E5E5",
              }}
            >
              {ini}
            </div>
          ) : null}
        </div>
      );
    };

    // ── Render a scheduled-slot tile (per-timeslot row, each clickable) ──────
    const renderScheduledSlotTile = (card, idx) => {
      const _rawSlotTime = getActiveSlotForDate(card?.timeSlot, date);
      const slotTime = typeof _rawSlotTime === "string" ? _rawSlotTime : "";
      const cardStatus = getSpecificMedicationStatus(
        card?.medicationType,
        date,
        card?.timeSlot,
      );
      const slotColor =
        getColor(cardStatus || "upcoming") || getColor("upcoming");
      const staffName = card?.staff?.name || "";
      const ini = staffName
        ? staffName
            .split(" ")
            .filter(Boolean)
            .map((w) => w[0])
            .join("")
            .toUpperCase()
            .slice(0, 2)
        : "";
      return (
        <div
          key={idx}
          className={classes.tintedAdminTile}
          style={{
            borderColor: slotColor?.border || "#E5E5E5",
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleClick(card);
          }}
        >
          <span
            className={classes.tintedAdminTime}
            style={{ color: slotColor?.dark || "#404040", flex: 1 }}
          >
            {slotTime}
          </span>
          {ini ? (
            <div
              className={classes.tintedInitialsAvatar}
              style={{
                backgroundColor: slotColor?.light || "#F5F5F5",
                color: slotColor?.dark || "#404040",
                borderColor: slotColor?.dark || "#E5E5E5",
              }}
            >
              {ini}
            </div>
          ) : null}
        </div>
      );
    };

    // MarTab hoists comments to top-level on every card — read it directly
    const getSlotComment = (card) => card?.comments || null;

    // ── Multi-slot scheduled cell (no status badge, each tile clickable) ─────
    if (allScheduledCards) {
      const neutralColor = getColor("upcoming");
      const VISIBLE_SLOTS = 2;
      const visibleCards = allScheduledCards.slice(0, VISIBLE_SLOTS);
      const hiddenCount = allScheduledCards.length - VISIBLE_SLOTS;
      return (
        <>
          <div
            className={classes.tintedCell}
            style={{
              backgroundColor: "#F5F5F5",
              borderColor: "#E5E5E5",
            }}
          >
            {/* ×N badge — static row so tiles don't overlap */}
            <div className={classes.tintedExtrasBadgeRow}>
              <span
                className={classes.tintedExtrasBadge}
                style={{ backgroundColor: neutralColor?.dark || "#404040" }}
              >
                ×{allScheduledCards.length}
              </span>
            </div>

            {/* Med details strip — shown when label column is collapsed (no dosage count for multi-slot) */}
            {showMedDetails && (() => {
              const isNarcotic = data?.form3?.isNarcotic === "Yes";
              const ingestionMode = data?.form3?.ingestionMode || "";
              const dosageForm = data?.form3?.dosageForm || "";
              const strengthParts = [
                dosage,
                dosageForm ? ` · ${dosageForm}` : "",
                ingestionMode ? `, ${ingestionMode.toLowerCase()}` : "",
              ].join("");
              return (
                <div className={classes.tintedMedLabel}>
                  <div className={classes.tintedMedStrip}>
                    <div className={classes.tintedMedNameRow}>
                      <div
                        className={`${classes.tintedMedPillIconBox} ${
                          isNarcotic
                            ? classes.tintedMedPillIconBoxNarcotic
                            : classes.tintedMedPillIconBoxNormal
                        }`}
                      >
                        <LuPill
                          size={10}
                          color={isNarcotic ? "#B91C1C" : "#92400E"}
                        />
                      </div>
                      <span className={classes.tintedMedName}>{medName}</span>
                    </div>
                    {strengthParts && (
                      <span className={classes.tintedMedStrengthRow}>
                        {strengthParts}
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* No status pill — each slot may have a different status */}
            <div className={classes.tintedBody} style={{ paddingTop: 0 }}>
              {visibleCards.map((card, i) => renderScheduledSlotTile(card, i))}
              {hiddenCount > 0 && (
                <div onClick={(e) => e.stopPropagation()}>
                  <MarDetailModal
                    data={allScheduledCards}
                    calendar="daily"
                    date={moment(date, "MM/DD/YYYY").format("MMMM DD, YYYY")}
                    triggerLabel={`+${hiddenCount} more`}
                  />
                </div>
              )}
            </div>
          </div>

          <MedicationModal
            opened={opened}
            onclose={() => {
              close();
              setIsViewOpen(false);
              setActiveSlotCard(null);
            }}
            isViewOpen={isViewOpen}
            setIsViewOpen={setIsViewOpen}
            data={activeSlotCard || data}
            allData={allData}
            date={date}
            futureDate={compareDate((activeSlotCard || data)?.date)}
          />
        </>
      );
    }

    return (
      <>
        <div
          className={classes.tintedCell}
          style={{
            backgroundColor: colorSet?.light || "#FFF",
            borderColor: colorSet?.border || "#E5E5E5",
          }}
          onClick={prn && extras.length > 0 ? undefined : () => handleClick()}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colorSet?.dark || "#171717";
            e.currentTarget.style.boxShadow = "0 1px 3px rgba(16,24,40,0.08)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = colorSet?.border || "#E5E5E5";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          {/* ×N badge — absolute top-right for multiple PRN admins in same slot */}
          {totalCount > 1 && (
            <span
              className={classes.tintedExtrasBadge}
              style={{
                backgroundColor: colorSet?.dark || "#404040",
                position: "absolute",
                top: "6px",
                right: "6px",
              }}
            >
              ×{totalCount}
            </span>
          )}

          {/* Med details strip — shown when medication label column is collapsed */}
          {showMedDetails && (() => {
            const isNarcotic = data?.form3?.isNarcotic === "Yes";
            const ingestionMode = data?.form3?.ingestionMode || "";
            const dosageForm = data?.form3?.dosageForm || "";
            const strengthParts = [
              dosage,
              dosageForm ? ` · ${dosageForm}` : "",
              ingestionMode ? `, ${ingestionMode.toLowerCase()}` : "",
            ].join("");
            const administeredQty = (() => {
              const mt = data?.medicationType;
              if (Array.isArray(mt) && mt.length > 0) {
                const entrySlot = getActiveSlotForDate(data?.timeSlot, date);
                const match =
                  mt.filter((m) => m.timeSlot === entrySlot && m.dosage != null).pop() ||
                  mt.filter((m) => m.dosage != null).pop();
                if (match?.dosage != null) return match.dosage;
              }
              return data?.dosage ?? null;
            })();
            const adminLabel = administeredQty != null
              ? `Administer: ${administeredQty} ${dosageForm || ""}${Number(administeredQty) === 1 ? "" : "s"}`
              : null;
            return (
              <div className={classes.tintedMedLabel}>
                <div className={classes.tintedMedStrip}>
                  <div className={classes.tintedMedNameRow}>
                    <div
                      className={`${classes.tintedMedPillIconBox} ${
                        isNarcotic
                          ? classes.tintedMedPillIconBoxNarcotic
                          : classes.tintedMedPillIconBoxNormal
                      }`}
                    >
                      <LuPill
                        size={10}
                        color={isNarcotic ? "#B91C1C" : "#92400E"}
                      />
                    </div>
                    <span className={classes.tintedMedName}>{medName}</span>
                  </div>
                  {strengthParts && (
                    <span className={classes.tintedMedStrengthRow}>
                      {strengthParts}
                    </span>
                  )}
                  {adminLabel && (status === "Taken" || prn) && (
                    <span className={classes.tintedMedAdminRow}>
                      {adminLabel}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Status pill row */}
          <div className={classes.tintedPillRow}>
            <div
              className={classes.tintedStatusPill}
              style={{ backgroundColor: colorSet?.dark || "#404040" }}
            >
              <span
                className={classes.tintedPillIcon}
                style={{
                  backgroundColor: colorSet?.light || "rgba(255,255,255,0.22)",
                }}
              >
                {status === "Taken" || status === "PRN" ? (
                  <FiCheck
                    size={9}
                    color={colorSet?.dark || "#404040"}
                    strokeWidth={3}
                  />
                ) : status === "Missed" || status === "Out of Medication" ? (
                  <FiX
                    size={9}
                    color={colorSet?.dark || "#404040"}
                    strokeWidth={3}
                  />
                ) : status === "Refused" || status === "Unable to Take" ? (
                  <FiAlertTriangle
                    size={9}
                    color={colorSet?.dark || "#404040"}
                    strokeWidth={3}
                  />
                ) : (
                  <FiClock
                    size={9}
                    color={colorSet?.dark || "#404040"}
                    strokeWidth={3}
                  />
                )}
              </span>
              <span>{statusLabel}</span>
            </div>
          </div>

          {/* Divider */}
          <div
            className={classes.tintedDivider}
            style={{ borderTopColor: colorSet?.border || "#E5E5E5" }}
          />

          {/* Bottom section */}
          <div className={classes.tintedBody}>
            {showAdminTile || extras.length > 0 ? (
              <>
                {renderAdminTile(data, 0)}
                {extras
                  .slice(0, 1)
                  .map((extra, i) => renderAdminTile(extra, i + 1))}
                {extras.length > 1 && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <MarDetailModal
                      data={[data, ...extras]}
                      calendar="daily"
                      date={moment(date, "MM/DD/YYYY").format("MMMM DD, YYYY")}
                      triggerLabel={`+${extras.length - 1} more`}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className={classes.tintedNoAdmin}>
                <span
                  className={classes.tintedAwaitingText}
                  style={{ color: colorSet?.dark || "#737373" }}
                >
                  {isUpcoming ? "Awaiting" : "Unsigned"}
                </span>
                {timeLabel && (
                  <span
                    className={classes.tintedAwaitingTime}
                    style={{ color: colorSet?.dark || "#404040" }}
                  >
                    {timeLabel}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Comment — only for single-slot, non-Missed, non-upcoming */}
          {!isMissed &&
            !isUpcoming &&
            extras.length === 0 &&
            (() => {
              const comment = getSlotComment(data);
              if (!comment) return null;
              return (
                <div
                  style={{
                    paddingTop: 8,
                    borderTop: `1px solid ${colorSet?.border || "#E5E5E5"}`,
                    marginTop: 2,
                  }}
                >
                  <div
                    title={comment}
                    style={{
                      fontSize: 10,
                      color: colorSet?.dark || "#404040",
                      fontStyle: "italic",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      textOverflow: "ellipsis",
                      lineHeight: 1.3,
                      textAlign: "center",
                      wordBreak: "break-word",
                    }}
                  >
                    {comment}
                  </div>
                </div>
              );
            })()}
        </div>

        <MedicationModal
          opened={opened}
          onclose={() => {
            close();
            setIsViewOpen(false);
            setActiveSlotCard(null);
          }}
          isViewOpen={isViewOpen}
          setIsViewOpen={setIsViewOpen}
          data={activeSlotCard || data}
          allData={allData}
          date={date}
          futureDate={compareDate((activeSlotCard || data)?.date)}
        />
      </>
    );
  }

  // ── Original monthly UI ───────────────────────────────────────────────
  return (
    <>
      <div
        id="mar-scheduled"
        onClick={() => handleClick()}
        className={`${classes.scheduleCard} monthcard`}
      >
        <div className="card-frequency">
          <span className="freq-name">{data?.form3?.frequency || "-"}</span>
          {data?.form3?.isNarcotic === "Yes" && (
            <span className="narcotic-badge">Narcotic</span>
          )}
        </div>

        <p className="card-time">
          {prn
            ? data?.administeredAt
              ? moment(data.administeredAt)
                  .tz(timeZone || moment.tz.guess())
                  .format("hh:mm A")
              : "-"
            : getActiveSlotForDate(data?.timeSlot, date) || "-"}
        </p>
        <div className="Card-med-border"></div>

        <div className="med-dosage-group">
          <p className="card-med-name">{medNameDisplay}</p>
          {formDisplay && <p className="card-med-form">{formDisplay}</p>}
        </div>

        <div className="card-status">
          <div className={classes.statusRow}>
            <img
              src={prn ? prnMed : getStatusImage(_medicationType)}
              className={classes.statusImg}
              alt={_medicationType}
            />
            <span
              className={`${classes.statusText} ${
                _medicationType === "Taken"
                  ? classes.statusTaken
                  : _medicationType === "Refused"
                    ? classes.statusRefused
                    : _medicationType === "Unable to Take"
                      ? classes.statusUnable
                      : _medicationType === "Missed"
                        ? classes.statusMissed
                        : prn
                          ? classes.statusPrn
                          : classes.statusDefault
              }`}
            >
              {_medicationType === "upcoming" ? "Upcoming" : _medicationType}
            </span>
          </div>
        </div>
      </div>

      <MedicationModal
        opened={opened}
        onclose={() => {
          close();
          setIsViewOpen(false);
        }}
        isViewOpen={isViewOpen}
        setIsViewOpen={setIsViewOpen}
        data={data}
        allData={allData}
        date={date}
        futureDate={compareDate(data?.date)}
      />
    </>
  );
};

export default React.memo(ScheduleCard);
